import { useState, useRef } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { api, useAuth } from '../context/AuthContext'

const INP = { height: 44, borderRadius: 9, border: '1px solid #e9e9e4', padding: '0 14px', fontSize: 13, outline: 'none', width: '100%', boxSizing: 'border-box', fontFamily: "'Plus Jakarta Sans',sans-serif", background: '#fff' }
const LBL = { display: 'block', fontSize: 11, fontWeight: 700, color: '#374151', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 5, fontFamily: "'JetBrains Mono',monospace" }

export default function ForgotPassword() {
  const { login } = useAuth()
  const navigate  = useNavigate()

  const [step,      setStep]      = useState(1) // 1=phone, 2=otp, 3=new-password
  const [phone,     setPhone]     = useState('')
  const [otp,       setOtp]       = useState('')
  const [newPass,   setNewPass]   = useState('')
  const [confirm,   setConfirm]   = useState('')
  const [showPass,  setShowPass]  = useState(false)
  const [saving,    setSaving]    = useState(false)
  const [error,     setError]     = useState('')
  const [countdown, setCountdown] = useState(0)
  const resetTokenRef = useRef(null)
  const timerRef      = useRef(null)

  const startCountdown = () => {
    setCountdown(60)
    clearInterval(timerRef.current)
    timerRef.current = setInterval(() => {
      setCountdown(c => { if (c <= 1) { clearInterval(timerRef.current); return 0 } return c - 1 })
    }, 1000)
  }

  const handleSendOtp = async (e) => {
    e?.preventDefault()
    setError('')
    if (!phone.trim()) { setError('Enter your phone number.'); return }
    setSaving(true)
    try {
      await api.post('/user/otp/send', { phone: phone.trim() })
      setStep(2)
      startCountdown()
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to send code. Please try again.')
    } finally { setSaving(false) }
  }

  const handleVerifyOtp = async (e) => {
    e.preventDefault()
    setError('')
    if (!otp.trim()) { setError('Enter the 6-digit code.'); return }
    setSaving(true)
    try {
      const res = await api.post('/user/otp/verify', { phone: phone.trim(), code: otp.trim() })
      const token = res.data.token
      if (!token) { setError('Verification failed — no token returned.'); return }
      resetTokenRef.current = token
      setStep(3)
    } catch (err) {
      setError(err.response?.data?.error || 'Invalid or expired code.')
    } finally { setSaving(false) }
  }

  const handleSetPassword = async (e) => {
    e.preventDefault()
    setError('')
    if (!newPass || newPass.length < 6) { setError('Password must be at least 6 characters.'); return }
    if (newPass !== confirm)            { setError('Passwords do not match.'); return }
    setSaving(true)
    try {
      await api.put('/user/change-password', { newPassword: newPass }, {
        headers: { Authorization: `Bearer ${resetTokenRef.current}` },
      })
      // Auto-login if the account is a seller; otherwise just redirect to login
      if (login(resetTokenRef.current)) {
        navigate('/dashboard')
      } else {
        navigate('/login', { state: { message: 'Password updated. Please sign in.' } })
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update password.')
    } finally { setSaving(false) }
  }

  const STEP_LABELS = ['Phone', 'Verify', 'New Password']

  return (
    <div style={{ minHeight: '100vh', background: '#F6F6F3', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20, fontFamily: "'Plus Jakarta Sans',sans-serif" }}>
      <div style={{ width: '100%', maxWidth: 420 }}>

        {/* Logo */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 32, justifyContent: 'center' }}>
          <img src="/kudos_logo.png" alt="Kudos Live" style={{ width: 44, height: 44, borderRadius: 12, objectFit: 'contain' }}
            onError={e => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex' }} />
          <div style={{ width: 44, height: 44, borderRadius: 12, background: 'linear-gradient(145deg,#E8440A 0%,#c9380a 100%)', display: 'none', alignItems: 'center', justifyContent: 'center', boxShadow: '0 4px 14px rgba(232,68,10,0.3)' }}>
            <span style={{ fontSize: 20, fontWeight: 900, color: '#fff' }}>K</span>
          </div>
          <div>
            <div style={{ fontSize: 17, fontWeight: 800, letterSpacing: '-0.03em', color: '#111317' }}>Kudos Live</div>
            <div style={{ fontSize: 10, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace" }}>Seller Portal</div>
          </div>
        </div>

        <div style={{ background: '#fff', borderRadius: 16, border: '1px solid #e9e9e4', boxShadow: '0 4px 32px rgba(0,0,0,0.07)', overflow: 'hidden' }}>

          {/* Step indicator */}
          <div style={{ padding: '20px 28px 0', display: 'flex', alignItems: 'center', gap: 6 }}>
            {STEP_LABELS.map((lbl, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                <div style={{ width: 22, height: 22, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 800,
                  background: step > i + 1 ? '#15803d' : step === i + 1 ? '#E8440A' : '#f3f3f0',
                  color: step >= i + 1 ? '#fff' : '#9a9a94', fontFamily: "'JetBrains Mono',monospace" }}>
                  {step > i + 1 ? '✓' : i + 1}
                </div>
                <span style={{ fontSize: 10, fontWeight: 600, color: step >= i + 1 ? '#111317' : '#9a9a94' }}>{lbl}</span>
                {i < STEP_LABELS.length - 1 && <span style={{ color: '#d1d5db', fontSize: 12 }}>→</span>}
              </div>
            ))}
          </div>

          <div style={{ padding: '20px 28px 28px' }}>
            <div style={{ fontSize: 20, fontWeight: 800, marginBottom: 4, letterSpacing: '-0.3px' }}>
              {step === 1 ? 'Reset your password' : step === 2 ? 'Enter the code' : 'Set new password'}
            </div>
            <div style={{ fontSize: 13, color: '#9a9a94', marginBottom: 22 }}>
              {step === 1 ? 'We\'ll send a 6-digit code to your phone.' : step === 2 ? `Code sent to ${phone}` : 'Choose a strong password for your account.'}
            </div>

            {error && (
              <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '10px 14px', marginBottom: 16, fontSize: 13, color: '#b91c1c', fontWeight: 600 }}>
                ⚠ {error}
              </div>
            )}

            {/* Step 1 — phone */}
            {step === 1 && (
              <form onSubmit={handleSendOtp} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                <div>
                  <label style={LBL}>Phone Number</label>
                  <input value={phone} onChange={e => setPhone(e.target.value)} placeholder="+250 7XX XXX XXX" type="tel"
                    style={INP} autoFocus />
                </div>
                <button type="submit" disabled={saving}
                  style={{ height: 46, borderRadius: 10, border: 'none', background: saving ? '#f0c4b4' : '#E8440A', color: '#fff', fontSize: 14, fontWeight: 700, cursor: saving ? 'not-allowed' : 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif" }}>
                  {saving ? 'Sending…' : 'Send Code →'}
                </button>
              </form>
            )}

            {/* Step 2 — OTP */}
            {step === 2 && (
              <form onSubmit={handleVerifyOtp} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                <div>
                  <label style={LBL}>6-Digit Code</label>
                  <input value={otp} onChange={e => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="______" maxLength={6} autoFocus
                    style={{ ...INP, textAlign: 'center', fontSize: 24, fontWeight: 800, letterSpacing: '0.3em', fontFamily: "'JetBrains Mono',monospace" }} />
                </div>
                <button type="submit" disabled={saving || otp.length < 6}
                  style={{ height: 46, borderRadius: 10, border: 'none', background: (saving || otp.length < 6) ? '#f0c4b4' : '#E8440A', color: '#fff', fontSize: 14, fontWeight: 700, cursor: (saving || otp.length < 6) ? 'not-allowed' : 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif" }}>
                  {saving ? 'Verifying…' : 'Verify Code →'}
                </button>
                <div style={{ textAlign: 'center', fontSize: 12, color: '#9a9a94' }}>
                  {countdown > 0
                    ? `Resend in ${countdown}s`
                    : <span onClick={handleSendOtp} style={{ color: '#E8440A', fontWeight: 700, cursor: 'pointer' }}>Resend code</span>}
                </div>
              </form>
            )}

            {/* Step 3 — new password */}
            {step === 3 && (
              <form onSubmit={handleSetPassword} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                <div>
                  <label style={LBL}>New Password</label>
                  <div style={{ position: 'relative' }}>
                    <input value={newPass} onChange={e => setNewPass(e.target.value)} placeholder="Min. 6 characters"
                      type={showPass ? 'text' : 'password'} style={{ ...INP, paddingRight: 44 }} autoFocus autoComplete="new-password" />
                    <span onClick={() => setShowPass(p => !p)}
                      style={{ position: 'absolute', right: 13, top: 12, cursor: 'pointer', fontSize: 14, color: '#9a9a94', userSelect: 'none' }}>
                      {showPass ? '🙈' : '👁'}
                    </span>
                  </div>
                </div>
                <div>
                  <label style={LBL}>Confirm Password</label>
                  <input value={confirm} onChange={e => setConfirm(e.target.value)} placeholder="Repeat password"
                    type={showPass ? 'text' : 'password'}
                    style={{ ...INP, borderColor: confirm && confirm !== newPass ? '#fecaca' : '#e9e9e4' }}
                    autoComplete="new-password" />
                  {confirm && confirm !== newPass && (
                    <div style={{ fontSize: 11, color: '#b91c1c', marginTop: 4 }}>Passwords don't match</div>
                  )}
                </div>
                <button type="submit" disabled={saving}
                  style={{ height: 46, borderRadius: 10, border: 'none', background: saving ? '#f0c4b4' : '#15803d', color: '#fff', fontSize: 14, fontWeight: 700, cursor: saving ? 'not-allowed' : 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif" }}>
                  {saving ? 'Saving…' : '✓ Save New Password'}
                </button>
              </form>
            )}
          </div>
        </div>

        <div style={{ textAlign: 'center', marginTop: 20, fontSize: 13, color: '#9a9a94' }}>
          <Link to="/login" style={{ color: '#E8440A', fontWeight: 700, textDecoration: 'none' }}>← Back to Sign In</Link>
        </div>
      </div>
    </div>
  )
}
